import PropTypes from 'prop-types';
import React from 'react';

import { TeamIcon } from 'common/react/components/TeamIcon';
import { TeamWatermarkBackground } from 'common/react/components/TeamWatermarkBackground';
import { useSingleSiteSetting } from 'common/react/hooks/use-single-site-setting';
import { SvgIcon } from 'common/react/components/SvgIcon';

/**
 * @param matchRoster.matchRoster
 * @param {string} match The match details object.
 * @param {string} teamDisplay The team(s) to display, i.e. 'both', 'home', or 'away'.
 * @param matchRoster.match
 * @param matchRoster.teamDisplay
 * @returns
 */
export const TLCondensedHeader = ({ match, teamDisplay }) => {
    const {
        awayTeamId,
        homeTeamId,
        matchUrl,
        venueLandOwner,
        venueLocation,
        venueName,
        roundName,
        homeTeamName,
        awayTeamName
    } = match;

    const homeTeam =
            homeTeamName ||
            PULSE.app.common.common.team.getTeamName(homeTeamId),
        awayTeam =
            awayTeamName ||
            PULSE.app.common.common.team.getTeamName(awayTeamId),
        name =
            teamDisplay === 'both'
                ? `${homeTeam} v ${awayTeam}`
                : teamDisplay === 'home'
                ? `${homeTeam}`
                : `${awayTeam}`,
        selectedRegion = useSingleSiteSetting('region'),
        startTime = moment(match.startTime),
        homeAbbr = PULSE.app.common.team.getTeamAbbr(homeTeamId),
        awayAbbr = PULSE.app.common.team.getTeamAbbr(awayTeamId);

    const onClick = () => {
        const url = new URL(match.matchUrl, location.origin);

        /* eslint-disable camelcase */
        PULSE.app.tracking.recordEvent(
            PULSE.app.common.CONSTANTS.TRACKING.EVENTS.SELECT.LINK,
            {
                eventData: {
                    link_text: `${name}`,
                    link_url: match.matchUrl,
                    outbound: url.origin !== location.origin,
                    type:
                        url.origin !== location.origin
                            ? 'external'
                            : 'internal',
                    component_name: `${homeTeam} v ${awayTeam}`,
                    component_content_type:
                        PULSE.app.common.CONSTANTS.TRACKING.CONTENT_TYPES
                            .LINEUPS
                }
            }
        );
        /* eslint-enable camelcase */
    };

    return (
        <a
            className={`team-lineups-header team-lineups-header--${
                teamDisplay === `both` ? `both` : `single`
            }`}
            href={matchUrl}
            onClick={onClick}
        >
            {teamDisplay === 'both' && (
                <TeamWatermarkBackground
                    className="team-lineups-header__watermark team-lineups-header__watermark--home"
                    teamAbbreviation={homeAbbr}
                    side="left"
                    providerId={homeTeamId}
                />
            )}
            <TeamWatermarkBackground
                className="team-lineups-header__watermark team-lineups-header__watermark--away"
                teamAbbreviation={teamDisplay === 'home' ? homeAbbr : awayAbbr}
                providerId={awayTeamId}
            />
            {teamDisplay === 'both' && (
                <div className="team-lineups-header__details u-hide-from-tablet">
                    <span className="team-lineups-header__name">
                        {name.split(' ').slice(0, -1).join(' ')}{' '}
                        <span style={{ whiteSpace: 'nowrap' }}>
                            {name.split(' ').pop()}
                            <SvgIcon icon="arrow-right" className="icon" />
                        </span>
                    </span>
                </div>
            )}
            <div className="team-lineups-header__content">
                <div className="team-lineups-header__logo-wrapper">
                    <TeamIcon
                        providerId={
                            teamDisplay !== 'away' ? homeTeamId : awayTeamId
                        }
                        className="team-lineups-header__logo"
                        theme="light"
                    />
                </div>
                <div className="team-lineups-header__details">
                    <span
                        className={`team-lineups-header__name ${
                            teamDisplay === 'both' && `u-hide-until-tablet`
                        }`}
                    >
                        {name.split(' ').slice(0, -1).join(' ')}{' '}
                        <span style={{ whiteSpace: 'nowrap' }}>
                            {name.split(' ').pop()}
                            {teamDisplay === 'both' && (
                                <SvgIcon icon="arrow-right" className="icon" />
                            )}
                        </span>
                    </span>
                    <div className="team-lineups-header__info team-lineups-header__info--round">
                        <span className="team-lineups-header__info-round-label">
                            {roundName}{' '}
                            {teamDisplay === 'home'
                                ? ` v ${awayTeam}`
                                : teamDisplay === 'away'
                                ? ` v ${homeTeam}`
                                : null}
                        </span>
                        <span className="u-hide-until-tablet">{'\uFF65'}</span>
                        <span className="team-lineups-header__info team-lineups-header__info--date">
                            {startTime
                                .tz(selectedRegion.timezone)
                                .format('ddd D MMM YYYY')}
                            ,&nbsp;
                            {startTime
                                .tz(selectedRegion.timezone)
                                .format(
                                    PULSE.app.common.CONSTANTS.MATCH_TIME_FORMAT
                                )}
                            {startTime.tz(selectedRegion.timezone).format('a')}
                        </span>
                    </div>
                    <div
                        className={`team-lineups-header__info team-lineups-header__info--location ${
                            teamDisplay === `both` && `u-hide-until-tablet`
                        }`}
                    >
                        <span className="team-lineups-header__info">
                            {venueName}, {venueLocation}
                        </span>
                        {'\uFF65'}
                        <span>{venueLandOwner}</span>
                    </div>
                </div>
                {teamDisplay === 'both' && (
                    <div className="team-lineups-header__logo-wrapper">
                        <TeamIcon
                            providerId={awayTeamId}
                            className="team-lineups-header__logo"
                            theme="light"
                        />
                    </div>
                )}
            </div>
            {teamDisplay === 'both' && (
                <div className="team-lineups-header__info team-lineups-header__info--location u-hide-from-tablet">
                    <span>
                        {' '}
                        {venueName}, {venueLocation} {'\uFF65'} {venueLandOwner}
                    </span>
                </div>
            )}
        </a>
    );
};

TLCondensedHeader.propTypes = {
    match: PropTypes.object.isRequired,
    teamDisplay: PropTypes.string.isRequired
};
